import * as React from 'react';
import { graphql } from 'react-apollo';

import gql from 'graphql-tag';
import type { Moment } from 'moment';
import { compose, withHandlers, withProps, withState } from 'recompose';

import deferToClientSideRender from 'js/lib/deferToClientSideRender';
import mapProps from 'js/lib/mapProps';
import { tupleToStringKey } from 'js/lib/stringKeyTuple';
import user from 'js/lib/user';
import { momentWithUserTimezone } from 'js/utils/DateTimeUtils';

import { TabPanel } from '@coursera/cds-core';
import { Card } from '@coursera/coursera-ui';

import CalendarSyncCard from 'bundles/calendar-sync/CalendarSyncCard';
import Course from 'bundles/degree-home/components/Course';
import DegreeCoursesList from 'bundles/degree-home/components/DegreeCoursesList';
import EstimatedTimeDisplay from 'bundles/degree-home/components/EstimatedTimeDisplay';
import SlackAccountInfoCard from 'bundles/degree-home/components/SlackAccountInfoCard';
import SupportInfoCard from 'bundles/degree-home/components/SupportInfoCard';
import WeekSwitcher from 'bundles/degree-home/components/WeekSwitcher';
import type { DegreesV1 } from 'bundles/degree-home/components/__generated__/DegreesV1';
import type { LearningSequenceElement } from 'bundles/degree-home/types/Degree';

import 'css!./__styl__/DegreeCourses';

type Props = {
  degreeMetadata: DegreesV1;
  handleCurrentWeek: () => void;
  handleNextWeek: () => void;
  handlePreviousWeek: () => void;
  learningSequenceElements?: Array<LearningSequenceElement>;
  loading: boolean;
  supportEmailAddress: string;
  timeCommitment?: number;
  weekStartDate: Moment;
  weekEndDate: Moment;
};

export class DegreeCourses extends React.Component<Props> {
  render() {
    const {
      degreeMetadata,
      handleCurrentWeek,
      handleNextWeek,
      handlePreviousWeek,
      learningSequenceElements,
      loading,
      supportEmailAddress,
      timeCommitment,
      weekStartDate,
      weekEndDate,
    } = this.props;

    const degreeId = degreeMetadata.id;
    const partnerId = degreeMetadata.partnerIds[0];
    const supportLink = degreeMetadata.supportLink;

    return (
      <TabPanel value="home" data-e2e="degree-home-tab-content">
        <div className="row">
          <main className="bt3-col-xs-12 bt3-col-sm-12 bt3-col-md-6 bt3-col-lg-8 p-t-2">
            <Card>
              <div className="p-a-2">
                <WeekSwitcher
                  handleCurrentWeek={handleCurrentWeek}
                  handlePreviousWeek={handlePreviousWeek}
                  handleNextWeek={handleNextWeek}
                  weekStartDate={weekStartDate}
                  weekEndDate={weekEndDate}
                />

                <EstimatedTimeDisplay timeCommitment={timeCommitment} />
              </div>

              <DegreeCoursesList
                loading={loading}
                learningSequenceElements={learningSequenceElements}
                degreeId={degreeId}
              />
            </Card>
          </main>
          <aside className="bt3-col-xs-12 bt3-col-sm-12 bt3-col-md-6 bt3-col-lg-4 p-t-2">
            <SlackAccountInfoCard degreeId={degreeId} />
            <CalendarSyncCard asBanner={false} />
            <SupportInfoCard
              supportEmailAddress={supportEmailAddress}
              partnerId={partnerId}
              supportLink={supportLink}
            />
          </aside>
        </div>
      </TabPanel>
    );
  }
}

export const withWeekStartAndEndProps = withProps<
  { weekStartDate: Moment; weekEndDate: Moment },
  { weekOffset: number }
>(({ weekOffset }) => ({
  // moment's start of week is on Sunday. Add an additional day to make a degree home "week" start on Monday.
  weekStartDate: momentWithUserTimezone().startOf('week').add(1, 'day').add(weekOffset, 'week'),
  weekEndDate: momentWithUserTimezone().endOf('week').add(1, 'day').add(weekOffset, 'week'),
}));

export const withDegreeMetadata = withProps<{ maxWeekOffset: number; degreeId: string }, { degreeMetadata: DegreesV1 }>(
  ({ degreeMetadata }) => ({
    maxWeekOffset: 208, // prevent user from going 4 years plus or minus away from the current week
    degreeId: degreeMetadata.id,
  })
);

export const withWeekStartAndEndHandlers = withHandlers<
  {
    weekOffset: number;
    maxWeekOffset: number;
    updateWeekOffset: (weekOffset: number) => () => {};
  },
  {
    handleCurrentWeek: () => {};
    handlePreviousWeek: () => void;
    handleNextWeek: () => void;
  }
>({
  handleCurrentWeek:
    ({ updateWeekOffset }) =>
    () =>
      updateWeekOffset(0),
  handlePreviousWeek:
    ({ weekOffset, updateWeekOffset, maxWeekOffset }) =>
    () => {
      if (weekOffset > maxWeekOffset * -1) updateWeekOffset(weekOffset - 1);
    },
  handleNextWeek:
    ({ weekOffset, updateWeekOffset, maxWeekOffset }) =>
    () => {
      if (weekOffset < maxWeekOffset) updateWeekOffset(weekOffset + 1);
    },
});

export default compose<Props, {}>(
  deferToClientSideRender,
  withDegreeMetadata,
  withState('weekOffset', 'updateWeekOffset', 0),
  withWeekStartAndEndHandlers,
  withWeekStartAndEndProps,
  mapProps<{ id: string }, { degreeMetadata: { id: string }; weekStartDate: Moment; weekEndDate: Moment }>((props) => ({
    id: tupleToStringKey([
      tupleToStringKey([user.get().id, props.degreeMetadata.id]),
      props.weekStartDate.valueOf(),
      props.weekEndDate.valueOf(),
    ]),
  })),
  graphql(
    gql`
      query DegreeHomeProgressQuery($id: String!) {
        DegreeLearnerProgressesV1Resource {
          get(id: $id) {
            id
            timeCommitment
            ...DegreeCoursesListFragment
          }
        }
      }
      ${DegreeCoursesList.fragment}
      ${Course.fragment}
    `,
    {
      options: () => ({
        fetchPolicy: 'cache-and-network',
      }),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      props: ({ data: { loading, DegreeLearnerProgressesV1Resource } }: any) => ({
        loading,
        learningSequenceElements: DegreeLearnerProgressesV1Resource?.get?.learningSequenceElements,
        timeCommitment: DegreeLearnerProgressesV1Resource?.get?.timeCommitment,
      }),
    }
  )
)(DegreeCourses);
